import { useTranslation } from '../../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { Form, Select, Input, Checkbox, Row, Col, Spin, Typography } from 'antd';
import registrarService from '../../../services/registrarService';

const { Text } = Typography;
const isBool = (f) => f && (f.type === 'checkbox' || f.type === 'boolean');
const { Option } = Select;

/**
 * TLD Additional Fields Component
 *
 * Handles TLD-specific fields with automatic pre-filling from contact templates
 *
 * @param {string} domain - Full domain name (e.g., 'example.us')
 * @param {number} contactId - Selected contact template ID
 * @param {object} contactData - Full contact data (including extension_fields)
 * @param {function} onChange - Callback when fields change
 * Identity values typed here are saved back to the chosen address-book entry by the backend (cart default).
 */
const TldAdditionalFields = ({
    domain,
    contactId,
    contactData,
    onChange,
    operation = 'register',
    registrarId = null,
    tld: tldProp = '',
    onValidity
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [autoFilledFields, setAutoFilledFields] = useState(new Set());
    const [tld, setTld] = useState('');
    const [contactExtension, setContactExtension] = useState({});
    const [values, setValues] = useState({});

    // Extract TLD from domain
    // The cart row's own TLD (metadata.tld) wins: name-based guessing breaks on com.tr / com.br / co.nz …
    useEffect(() => {
        if (tldProp) {
            setTld(String(tldProp).replace(/^\./, '').toLowerCase());
        } else if (domain) {
            setTld(extractTld(domain));
        }
    }, [domain, tldProp]);

    // Load field configuration when TLD changes
    useEffect(() => {
        if (tld) {
            loadFieldsConfig();
        }
    }, [tld]);

    // Load contact extension fields when contact changes
    useEffect(() => {
        const loadContactExtension = async () => {
            if (!contactId || !tld) {
                setContactExtension({});
                return;
            }

            // 优先使用传入的 contactData
            const ext = contactData?.extension_fields?.[tld];
            if (ext) {
                setContactExtension(ext);
                return;
            }

            try {
                const response = await registrarService.getContactExtensionFields(contactId, tld);
                if (response.success) {
                    setContactExtension(response.data || {});
                } else {
                    setContactExtension({});
                }
            } catch (e) {
                setContactExtension({});
            }
        };

        loadContactExtension();
    }, [contactId, contactData, tld]);

    // Auto-fill from contact when fields or extension change
    useEffect(() => {
        if (contactId && tld && fieldsConfig.length > 0) {
            autoFillFromContact();
        }
    }, [contactId, tld, fieldsConfig, contactExtension]);

    /**
     * Extract TLD from domain name
     */
    const extractTld = (domainName) => {
        const parts = domainName.split('.');
        if (parts.length < 2) return '';

        // Handle two-level TLDs like .com.au, .co.uk
        const twoLevelTlds = ['com.au', 'net.au', 'org.au', 'co.uk', 'org.uk', 'me.uk'];
        const last = parts[parts.length - 1];
        const secondLast = parts[parts.length - 2];
        const twoLevel = `${secondLast}.${last}`;

        if (twoLevelTlds.includes(twoLevel)) {
            return twoLevel;
        }

        return last;
    };

    /**
     * Load field configuration for the TLD
     */
    const loadFieldsConfig = async () => {
        setLoading(true);
        try {
            const response = await registrarService.getAdditionalFields(tld, registrarId, operation);
            if (response.success) {
                // data 可能是数组,也可能是对象(某些注册商/TLD 返回 {}),统一归一为数组防止 .map 崩溃
                const raw = response.data;
                const arr = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
                // options 统一成 [{value, label}]。各注册商插件格式不一：Enom 给 [{value,label}]，
                // OpenSRS/Openprovider/NameSilo/DomainNameAPI 给 {代码: 名称}（PHP assoc→JSON object）。
                // 原来对象分支用 Object.values() 只剩名称字符串 → opt.value/opt.label 都是 undefined，
                // 下拉框全空、选了也提交不出代码（.us/.ca/.uk 等必填项无法填写）。
                const toOptions = (opts) => {
                    if (Array.isArray(opts)) {
                        return opts.map((o) => (o && typeof o === 'object')
                            ? { value: o.value ?? o.key ?? o.id, label: o.label ?? o.name ?? String(o.value ?? o.key ?? '') }
                            : { value: o, label: String(o) });
                    }
                    if (opts && typeof opts === 'object') {
                        return Object.entries(opts).map(([value, label]) => ({
                            value,
                            label: (label && typeof label === 'object') ? (label.label ?? label.name ?? value) : String(label),
                        }));
                    }
                    return [];
                };
                const normalized = arr.map((f) => (f && f.options ? { ...f, options: toOptions(f.options) } : f));
                setFieldsConfig(normalized);
                // registry/plugin defaults (e.g. a preselected language or a "No" opt-out)
                const defaults = {};
                normalized.forEach((f) => {
                    if (f && f.name && f.default !== undefined && f.default !== null && form.getFieldValue(f.name) === undefined) {
                        defaults[f.name] = isBool(f) ? [true, 1, '1', 'true', 'yes', 'Y'].includes(f.default) : f.default;
                    }
                });
                if (Object.keys(defaults).length) {
                    form.setFieldsValue(defaults);
                    const all = form.getFieldsValue(true);
                    setValues(all);
                    if (onChange) onChange(all);
                }
            } else {
                setFieldsConfig([]);
            }
        } catch (error) {
            console.error('Failed to load additional fields:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Auto-fill form from contact extension fields
     */
    const autoFillFromContact = () => {
        const contactExtFields = contactExtension || {};
        const filledFields = new Set();
        const formValues = {};

        fieldsConfig.forEach(field => {
            const scope = field.scope;
            if (scope === 'contact_extension') {
                const mappedFieldName = field.contact_field_mapping;
                if (mappedFieldName && contactExtFields[mappedFieldName] !== undefined) {
                    formValues[field.name] = contactExtFields[mappedFieldName];
                    filledFields.add(field.name);
                }
            } else if (scope === 'domain_additional') {
                // 域名级字段直接用字段名
                if (contactExtFields[field.name] !== undefined) {
                    formValues[field.name] = contactExtFields[field.name];
                    filledFields.add(field.name);
                }
            }
        });

        if (Object.keys(formValues).length > 0) {
            form.setFieldsValue(formValues);
            setAutoFilledFields(filledFields);

            // Notify parent component
            if (onChange) {
                onChange(formValues);
            }
        }
    };

    /**
     * Handle form field change
     */
    const handleFieldChange = (changedValues, allValues) => {
        setValues(allValues);
        if (onChange) {
            onChange(allValues);
        }
    };

    // depends_on: {field: [values…]} — shown (and required) only while the controlling field matches
    const visible = (field) => {
        const dep = field && field.depends_on;
        if (!dep || typeof dep !== 'object') return true;
        return Object.entries(dep).every(([k, allowed]) => (Array.isArray(allowed) ? allowed : [allowed]).map(String).includes(String(values[k] ?? '')));
    };
    const shown = fieldsConfig.filter((f) => f && visible(f));

    // Tell the parent whether every required (visible) field is filled, so it can block "Confirm"
    useEffect(() => {
        if (!onValidity) return;
        const missing = shown.filter((f) => f.required && (isBool(f) ? values[f.name] !== true : (values[f.name] === undefined || values[f.name] === null || String(values[f.name]).trim() === '')));
        onValidity(missing.length === 0, missing.map((f) => f.name));
    }, [shown.length, values, fieldsConfig]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Render field based on type
     */
    const renderField = (field) => {
        const className = undefined;

        switch (field.type) {
            case 'select':
                return (
                    <Select
                        placeholder={t('Select {{label}}', { label: t(field.label) })}
                        className={className}
                        showSearch
                        optionFilterProp="children"
                    >
                        {(Array.isArray(field.options) ? field.options : []).map(opt => (
                            <Option key={opt.value} value={opt.value}>
                                {t(opt.label)}
                            </Option>
                        ))}
                    </Select>
                );

            case 'checkbox':
            case 'boolean':
                return (
                    <Checkbox className={className}>
                        {t(field.hint || field.label)}
                    </Checkbox>
                );

            case 'text':
            case 'date':
            default:
                return (
                    <Input
                        placeholder={field.hint ? t(field.hint) : t('Please enter {{label}}', { label: t(field.label) })}
                        className={className}
                        type={['date', 'email', 'url'].includes(field.type) ? field.type : 'text'}
                    />
                );
        }
    };

    if (!tld || shown.length === 0) {
        return null;
    }

    return (
        <div style={{ marginTop: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                {t('Required for {{domain}}', { domain })}
            </Text>
            <Spin spinning={loading}>
                <Form form={form} layout="vertical" requiredMark={false} onValuesChange={handleFieldChange}>
                    <Row gutter={12}>
                        {shown.map(field => (
                            <Col xs={24} sm={isBool(field) ? 24 : 12} key={field.name}>
                                <Form.Item
                                    name={field.name}
                                    label={isBool(field) ? undefined : t(field.label)}
                                    tooltip={field.hint && !isBool(field) ? t(field.hint) : undefined}
                                    rules={[isBool(field) && field.required
                                        ? { validator: (_, v) => (v === true ? Promise.resolve() : Promise.reject(new Error(t('Please confirm {{label}}', { label: t(field.label) })))) }
                                        : {
                                            required: field.required,
                                            message: field.type === 'select' ? t('Select {{label}}', { label: t(field.label) }) : t('Please enter {{label}}', { label: t(field.label) }),
                                        }]}
                                    valuePropName={isBool(field) ? 'checked' : 'value'}
                                    extra={autoFilledFields.has(field.name) ? t('Filled from address book') : undefined}
                                >
                                    {renderField(field)}
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>
                </Form>
            </Spin>
        </div>
    );
};

export default TldAdditionalFields;
